// src/components/CustomizationModal/index.js - FIXED ADDONS BUG
import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import SpiceLevel from '../SpiceLevel';
import './index.css';
import { settingsService } from '../../config/settingsService';

const formatPrice = (amount) => {
  return settingsService.formatPrice(amount);
};

const CustomizationModal = ({
  isOpen,
  onClose,
  product,
  onAddToCart,
  isEditing = false, // NEW: flag for edit mode
  existingCartItem = null, // NEW: existing cart item data
  isUserLoggedIn
}) => {
  const [selectedVariations, setSelectedVariations] = useState({});
  const [selectedModifiers, setSelectedModifiers] = useState({}); // { groupId: [modifierObjects] }
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [showValidation, setShowValidation] = useState(false);



  useEffect(() => {
    if (isOpen && product) {
      console.group('🎨 CUSTOMIZATION MODAL: Product Received');
      setShowValidation(false); // Reset validation on open
      console.log('📦 PRODUCT RECEIVED:', {
        id: product.id,
        name: product.name,
        price: product.price,
        flatPrice: product.flatPrice,
        hasVariations: !!product.variations && Object.keys(product.variations).length > 0,
        hasExtras: Array.isArray(product.extras) && product.extras.length > 0,
        hasAddons: Array.isArray(product.addons) && product.addons.length > 0,
        isEditing: !!isEditing
      });
      console.groupEnd();

      if (isEditing && existingCartItem) {
        // EDIT MODE: Load existing selections
        console.group('✏️ EDIT MODE: Loading existing selections');
        console.log('Product:', product.name);
        console.log('Existing Cart Item:', existingCartItem);

        const existingVariations = {};

        if (existingCartItem.variations && Array.isArray(existingCartItem.variations)) {
          console.log('📌 Loading variations:', existingCartItem.variations);
          existingCartItem.variations.forEach(v => {
            // Find which attribute this variation belongs to
            if (product.variations && typeof product.variations === 'object') {
              Object.entries(product.variations).forEach(([attrId, variants]) => {
                const matchingVariant = variants.find(variant =>
                  variant.id === v.variation_id || variant.id === v.id
                );
                if (matchingVariant) {
                  console.log(`  ✅ Matched variation: ${matchingVariant.name} (₹${matchingVariant.price})`);
                  existingVariations[attrId] = matchingVariant;
                }
              });
            }
          });
        }

        // ✅ FIXED: Match addons from existingCartItem with product.addons using ID first, then composite key
        const matchedAddons = [];
        if (existingCartItem.addons && Array.isArray(existingCartItem.addons)) {
          console.log('📌 Loading addons:', existingCartItem.addons);
          console.log('📌 Product addons:', product.addons);

          existingCartItem.addons.forEach(cartAddon => {
            // STRATEGY 1: Match by unique ID (most reliable)
            let matchingAddon = product.addons?.find(productAddon =>
              productAddon.id === cartAddon.id
            );

            // STRATEGY 2: Fallback to composite key (addon_id + addon_item_id)
            if (!matchingAddon) {
              console.log(`⚠️ ID match failed for addon ${cartAddon.addon_item_name} (ID: ${cartAddon.id}). Trying composite key...`);
              matchingAddon = product.addons?.find(productAddon =>
                Number(productAddon.addon_id) === Number(cartAddon.addon_id) &&
                Number(productAddon.addon_item_id) === Number(cartAddon.addon_item_id)
              );
            }

            if (matchingAddon) {
              console.log(`  ✅ Matched addon: ${matchingAddon.addon_item_name} (ID: ${matchingAddon.id})`);
              matchedAddons.push(matchingAddon);
            } else {
              console.warn(`  ❌ Copying addon directly (No match found in product): ${cartAddon.addon_item_name}`);
              // Fallback: Use the cart addon directly if it can't be matched (preserves selection visually)
              // We reconstruct it to ensure it has necessary styling properties if needed, 
              // but ideally it should match a product addon.
              matchedAddons.push(cartAddon);
            }
          });
        }

        // Match extras/modifiers back to their groups
        const mappedModifiers = {};
        if (existingCartItem.extras && Array.isArray(existingCartItem.extras)) {
          console.log('📌 Loading extras/modifiers:', existingCartItem.extras);

          // If we have modifierGroups in product, try to map back to them
          if (product.modifierGroups && product.modifierGroups.length > 0) {
            existingCartItem.extras.forEach(e => {
              // Try to find the group this extra belongs to
              let groupId = e.modifier_group_id;

              // If group ID is missing in cart item, try to find it in product data
              if (!groupId) {
                const foundGroup = product.modifierGroups.find(group =>
                  group.modifiers?.some(m => m.id === e.id)
                );
                groupId = foundGroup?.id;
              }

              if (groupId) {
                if (!mappedModifiers[groupId]) mappedModifiers[groupId] = [];
                mappedModifiers[groupId].push(e);
                console.log(`  ✅ Matched modifier "${e.name}" to group ID ${groupId}`);
              } else {
                // Fallback for ungrouped extras
                if (!mappedModifiers['ungrouped']) mappedModifiers['ungrouped'] = [];
                mappedModifiers['ungrouped'].push(e);
                console.log(`  ⚠️ Modifier "${e.name}" had no group, placing in 'ungrouped'`);
              }
            });
          } else {
            // Fallback: product doesn't have groups, keep all in 'ungrouped'
            mappedModifiers['ungrouped'] = existingCartItem.extras;
          }
        }

        setSelectedVariations(existingVariations);
        setSelectedModifiers(mappedModifiers);
        setSelectedAddons(matchedAddons);

        console.log('📦 EDIT MODE STATE SET:', {
          variations: Object.keys(existingVariations).length,
          modifiers: Object.keys(mappedModifiers).length,
          addons: matchedAddons.length
        });
        console.groupEnd();
      } else {
        // ADD MODE: Initialize with defaults
        console.group('➕ ADD MODE: Initialize with defaults');
        const defaultVariations = {};

        if (product.variations && typeof product.variations === 'object') {
          Object.entries(product.variations).forEach(([attrId, variants]) => {
            if (variants && variants.length > 0) {
              const defaultVar = variants[0];
              console.log(`  ✅ Default variation: ${defaultVar.name} (₹${defaultVar.price})`);
              defaultVariations[attrId] = defaultVar;
            }
          });
        }

        setSelectedVariations(defaultVariations);
        setSelectedModifiers({});
        setSelectedAddons([]);

        console.log('📦 ADD MODE STATE SET:', {
          variations: Object.keys(defaultVariations).length,
          modifiers: 0,
          addons: 0
        });
        console.groupEnd();
      }
    }
  }, [isOpen, product, isEditing, existingCartItem]);

  // ✅ FIXED: Memoize calculation BEFORE early return - must be unconditional
  const cachedTotal = useMemo(() => {
    const calculateTotalInternal = () => {
      console.group('💰 CustomizationModal: Calculating Total');

      let total = 0;

      // ✅ FIXED: Get base price first
      const basePrice = parseFloat(product?.price || 0);
      console.log('Base price: ' + formatPrice(basePrice));

      // STEP 1: ✅ FIXED: If variations exist, ADD the highest variation price to base
      // Example: Base ₹70 + Large variation +₹50 = ₹120
      const variationValues = Object.values(selectedVariations);

      if (variationValues.length > 0) {
        const highestVariationPrice = Math.max(
          ...variationValues.map(v => parseFloat(v.price || 0))
        );
        total = basePrice + highestVariationPrice;
        console.log('Using variation price: + ' + formatPrice(highestVariationPrice));
        console.log('Total after variation: ' + formatPrice(total));
      } else {
        total = basePrice;
        console.log('Using base price: ' + formatPrice(total));
      }

      // STEP 2: Add Modifiers/Extras
      const allSelectedModifiers = Object.values(selectedModifiers).flat();
      const modifiersTotal = allSelectedModifiers.reduce((sum, extra) => {
        const extraPrice = parseFloat(extra.price || 0);
        console.log(`  + Modifier "${extra.name}": ${formatPrice(extraPrice)}`);
        return sum + extraPrice;
      }, 0);

      total += modifiersTotal;

      // STEP 3: Add Addons
      const addonsTotal = selectedAddons.reduce((sum, addon) => {
        // ✅ FIXED: Use pre-calculated total_flat_price from API
        const addonPrice = parseFloat(addon.total_flat_price || addon.total || addon.addon_item_price || 0);
        console.log(`  + Addon "${addon.addon_item_name}": ${formatPrice(addonPrice)}`);
        return sum + addonPrice;
      }, 0);

      total += addonsTotal;

      console.log('✅ Final Total: ' + formatPrice(total));
      console.groupEnd();

      return total; // Return number, not formatted string
    };

    return calculateTotalInternal();
  }, [product, selectedVariations, selectedModifiers, selectedAddons]);

  const ungroupedExtras = useMemo(() => {
    if (!product || !product.extras || !Array.isArray(product.extras)) return [];

    const hasGroups = product.modifierGroups &&
      Array.isArray(product.modifierGroups) &&
      product.modifierGroups.length > 0;

    if (!hasGroups) return product.extras;

    // Get all modifier IDs that are already in groups
    const groupedModifierIds = new Set();
    product.modifierGroups.forEach(group => {
      group.modifiers?.forEach(m => groupedModifierIds.add(m.id));
    });

    // Return extras that are not in any group
    return product.extras.filter(extra => !groupedModifierIds.has(extra.id));
  }, [product, product?.extras, product?.modifierGroups]);

  if (!isOpen || !product) return null;

  const hasVariations = product.variations &&
    typeof product.variations === 'object' &&
    Object.keys(product.variations).length > 0;

  const hasModifierGroups = product.modifierGroups &&
    Array.isArray(product.modifierGroups) &&
    product.modifierGroups.length > 0;

  const hasUngroupedExtras = ungroupedExtras.length > 0;

  const hasExtrasFallback = !hasModifierGroups && hasUngroupedExtras;

  const hasAddons = product.addons &&
    Array.isArray(product.addons) &&
    product.addons.length > 0;

  const handleModifierToggle = (group, modifier) => {
    const groupId = group?.id || 'ungrouped';
    const minSelections = group?.min_selections || 0;
    const maxSelections = group?.max_selections || null;

    setSelectedModifiers(prev => {
      const currentSelections = prev[groupId] || [];
      const isSelected = currentSelections.find(m => m.id === modifier.id);

      if (isSelected) {
        // Remove it
        return {
          ...prev,
          [groupId]: currentSelections.filter(m => m.id !== modifier.id)
        };
      } else {
        // Add it - check max selections
        if (maxSelections && currentSelections.length >= maxSelections) {
          if (maxSelections === 1) {
            // Radio-like behavior: replace the existing one
            return {
              ...prev,
              [groupId]: [modifier]
            };
          }
          // Already reached max for checkbox-like behavior
          return prev;
        }

        // Add to existing selections
        return {
          ...prev,
          [groupId]: [...currentSelections, modifier]
        };
      }
    });
  };

  const isGroupSatisfied = (group) => {
    // ✅ If the group has no modifiers, it shouldn't block validation
    if (!group.modifiers || group.modifiers.length === 0) return true;

    const groupId = group.id;
    const selectedCount = selectedModifiers[groupId]?.length || 0;
    const minSelections = parseInt(group.min_selections) || 0;

    return selectedCount >= minSelections;
  };

  const isAllValid = () => {
    // 1. Check all modifier groups
    if (hasModifierGroups) {
      const allGroupsSatisfied = product.modifierGroups.every(group => isGroupSatisfied(group));
      if (!allGroupsSatisfied) return false;
    }

    // 2. Check variations (one for each attribute)
    if (hasVariations) {
      const allVariationsSelected = Object.keys(product.variations).every(attrId => !!selectedVariations[attrId]);
      if (!allVariationsSelected) return false;
    }

    return true;
  };

  const isValid = isAllValid();

  const handleAddonToggle = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) {
        return prev.filter(a => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const handleVariationChange = (attrId, variant) => {
    setSelectedVariations(prev => ({
      ...prev,
      [attrId]: variant
    }));
  };

  const handleAddToCart = () => {
    // ✅ 0. Reset validation state on successful submission
    setShowValidation(false);

    console.group('🎨 CUSTOMIZATION MODAL: FORMATTING DATA');

    // ✅ Authentication check now handled in parent's handleCustomizedProductAdd

    // ✅ 1. Format Modifiers/Extras
    const allSelectedModifiers = Object.entries(selectedModifiers).flatMap(([groupId, modifiers]) => {
      return modifiers.map(m => ({ ...m, modifier_group_id: groupId === 'ungrouped' ? null : parseInt(groupId) }));
    });

    const formattedExtras = allSelectedModifiers.map(extra => {
      return {
        id: extra.id,
        extra_id: extra.id,
        name: extra.name,
        extra_name: extra.name,
        price: parseFloat(extra.price || 0),
        extra_price: parseFloat(extra.price || 0),
        modifier_group_id: extra.modifier_group_id
      };
    });

    // ✅ 2. Format Variations
    const formattedVariations = Object.values(selectedVariations).map(variant => {
      console.log('  Variation:', variant);
      return {
        id: variant.id,
        variation_id: variant.id,
        name: variant.name,
        variation_name: variant.item_attribute.name,
        price: parseFloat(variant.price || 0),
        variation_price: parseFloat(variant.price || 0)
      };
    });

    // ✅ 3. Format Addons - CRITICAL FIX
    const formattedAddons = selectedAddons.map(addon => {
      console.log('  Addon:', {
        id: addon.id,
        addon_id: addon.addon_id,
        addon_item_id: addon.addon_item_id,
        name: addon.addon_item_name,
        price: addon.addon_item_price
      });

      return {
        id: addon.id,                                      // Mapping ID (19, 20, 21...)
        addon_id: Number(addon.addon_id),                  // Parent item ID (126)
        addon_item_id: Number(addon.addon_item_id),        // Actual addon product ID (123...)
        addon_item_name: addon.addon_item_name,            // "Coca Cola"
        addon_item_price: parseFloat(addon.total_flat_price || addon.total || addon.addon_item_price || 0),
        price: parseFloat(addon.total_flat_price || addon.total || addon.addon_item_price || 0)     // Alias for consistency
      };
    });

    // ✅ 4. Calculate Total Price
    let totalPrice = 0;

    // ✅ FIXED: Base or Variation Price (add variation to base, don't replace)
    const variationValues = Object.values(selectedVariations);
    const basePrice = parseFloat(product.price || 0);

    console.log('🔍 BASE PRICE ANALYSIS:');
    console.log(`  product.price: ${product.price}`);
    console.log(`  product.flatPrice: ${product.flatPrice}`);
    console.log(`  Using for calculation: ${basePrice}`);

    console.log('PRICE CALCULATION DETAILS:');
    console.log(`  Base Price (product.price): ₹${basePrice}`);

    if (variationValues.length > 0) {
      const highestVariationPrice = Math.max(...variationValues.map(v => parseFloat(v.price || 0)));
      totalPrice = basePrice + highestVariationPrice;
      console.log(`  + Variation (highest): ₹${highestVariationPrice}`);
      console.log(`  = Subtotal: ₹${totalPrice}`);
    } else {
      totalPrice = basePrice;
      console.log('  No variations selected');
    }

    // Add Extras/Modifiers
    const extrasTotal = formattedExtras.reduce((sum, e) => sum + parseFloat(e.price || 0), 0);
    if (extrasTotal > 0) {
      console.log(`  + Modifiers: ₹${extrasTotal}`);
      totalPrice += extrasTotal;
    }

    // Add Addons
    const addonsTotal = selectedAddons.reduce((sum, a) => {
      return sum + parseFloat(a.total_flat_price || a.total || a.addon_item_price || 0);
    }, 0);
    if (addonsTotal > 0) {
      console.log(`  + Addons: ₹${addonsTotal}`);
      totalPrice += addonsTotal;
    }

    console.log(`  ✅ FINAL TOTAL PRICE: ₹${totalPrice}`);

    // ✅ 5. Build Customized Product
    const customizedProduct = {
      ...product,
      quantity: isEditing ? existingCartItem.quantity : 1,

      // Both formats for compatibility
      customizations: {
        variations: formattedVariations,
        extras: formattedExtras,
        addons: formattedAddons
      },

      // Direct properties (used by cart calculations)
      variations: formattedVariations,
      extras: formattedExtras,
      addons: formattedAddons,

      flatPrice: totalPrice,

      // ✅ CRITICAL: Only preserve cartItemKey when editing
      ...(isEditing && existingCartItem.cartItemKey
        ? { cartItemKey: existingCartItem.cartItemKey }
        : {}
      )
    };

    console.log('✅ FORMATTED PRODUCT:', {
      name: customizedProduct.name,
      isEditing,
      hasKey: !!customizedProduct.cartItemKey,
      variations: formattedVariations.length,
      extras: formattedExtras.length,
      addons: formattedAddons.length,
      flatPrice: totalPrice,
      totalPrice: totalPrice
    });

    // ✅ CRITICAL: Log what CartContent will receive
    console.log('📦 WHAT CART WILL RECEIVE:');
    console.log(`  flatPrice: ${totalPrice}`);
    console.log(`  variations array: ${formattedVariations.map(v => v.name).join(', ') || 'empty'}`);
    console.log(`  extras array: ${formattedExtras.map(e => e.name).join(', ') || 'empty'}`);
    console.log(`  addons array: ${formattedAddons.map(a => a.addon_item_name).join(', ') || 'empty'}`);
    console.log(`  NOTE: CartContent will detect customizations + flatPrice, so it will USE flatPrice directly`);

    // ✅ Log the complete product being sent
    console.log('📦 COMPLETE PRODUCT OBJECT:', customizedProduct);

    console.groupEnd();

    onAddToCart(customizedProduct, isEditing);
    onClose();
  };

  const getAttributeName = (attrId) => {
    const attr = product.itemAttributes?.find(a => a.id === parseInt(attrId));
    return attr?.name || 'Options';
  };

  // ✅ Calculate total price for a specific variant (base + variation price only)
  const getVariantTotalPrice = (variant) => {
    const basePrice = parseFloat(product.price || 0);
    const variantPrice = parseFloat(variant.price || 0);
    return basePrice + variantPrice;
  };

  return (
    <div className="customize-modal-overlay" onClick={onClose}>
      <div
        className="customize-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="customize-modal-header">
          <div className="customize-modal-header-content">
            <h2 className="customize-modal-title">
              {isEditing ? 'Edit Your Order' : (product.isCombo ? 'Customise Your Combo' : 'Customize Your Order')}
            </h2>
            <p className="customize-modal-subtitle">
              {product.name}
              {product.spiceLevel > 0 && (
                <span style={{ marginLeft: 8 }}><SpiceLevel level={product.spiceLevel} size={13} /></span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="customize-modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <div className="customize-modal-body">

          {hasVariations && Object.entries(product.variations).map(([attrId, variants]) => {
            if (!variants || variants.length === 0) return null;

            const attributeName = getAttributeName(attrId);

            return (
              <div key={attrId} className="customize-section">
                <h3 className="customize-section-title">
                  {attributeName}
                </h3>

                <div className="customize-options-list">
                  {variants.map((variant) => {
                    const isSelected = selectedVariations[attrId]?.id === variant.id;
                    const variantPrice = parseFloat(variant.price || 0);
                    const totalPrice = getVariantTotalPrice(variant);
                    const basePrice = parseFloat(product.price || 0);

                    return (
                      <div
                        key={variant.id}
                        onClick={() => handleVariationChange(attrId, variant)}
                        className={`customize-option-item ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="customize-option-left">
                          <div className={`customize-radio ${isSelected ? 'selected' : ''}`}>
                            {isSelected && <div className="customize-radio-dot" />}
                          </div>
                          <div className="customize-option-details">
                            <span className="customize-option-label">
                              {variant.name}
                            </span>
                            {/* ✅ Show breakdown: Base + Variation = Total */}
                            {/* <span className="customize-option-breakdown">
                              {formatPrice(basePrice)} + {formatPrice(variantPrice)} = {formatPrice(totalPrice)}
                            </span> */}
                          </div>
                        </div>

                        <span className="customize-option-price">
                          {formatPrice(totalPrice)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {hasModifierGroups && product.modifierGroups.map((group) => {
            // ✅ Hide the group entirely if it has no modifiers
            if (!group.modifiers || group.modifiers.length === 0) return null;

            const minSelections = parseInt(group.min_selections) || 0;
            const maxSelections = parseInt(group.max_selections) || 0;
            const isRequired = minSelections > 0;

            const satisfied = isGroupSatisfied(group);
            const selectedCount = selectedModifiers[group.id]?.length || 0;

            return (
              <div key={group.id} className={`customize-section ${showValidation && !satisfied ? 'not-satisfied' : ''}`}>
                <div className="customize-section-header">
                  <div className="customize-section-title-wrap">
                    <h3 className="customize-section-title">
                      {group.name}
                      {isRequired && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                    </h3>
                  </div>
                  <div className="customize-selection-info">
                    {minSelections > 0 && maxSelections > 0 ? (
                      <span>Select {minSelections === maxSelections ? minSelections : `${minSelections}-${maxSelections}`}</span>
                    ) : minSelections > 0 ? (
                      <span>Select min {minSelections}</span>
                    ) : maxSelections > 0 ? (
                      <span>Select max {maxSelections}</span>
                    ) : (
                      <span>Optional</span>
                    )}
                    <span className="selection-counter">({selectedCount}{maxSelections ? `/${maxSelections}` : ''})</span>
                  </div>
                </div>

                <div className="customize-options-list">
                  {group.modifiers?.map((modifier) => {
                    const isSelected = selectedModifiers[group.id]?.some(m => m.id === modifier.id);
                    const isMaxReached = group.max_selections && selectedCount >= group.max_selections && !isSelected;
                    const isRadio = group.max_selections === 1;

                    return (
                      <div
                        key={modifier.id}
                        onClick={() => handleModifierToggle(group, modifier)}
                        className={`customize-option-item ${isSelected ? 'selected' : ''} ${isMaxReached && !isRadio ? 'disabled-option' : ''}`}
                      >
                        <div className="customize-option-left">
                          {isRadio ? (
                            <div className={`customize-radio ${isSelected ? 'selected' : ''}`}>
                              {isSelected && <div className="customize-radio-dot" />}
                            </div>
                          ) : (
                            <div className={`customize-checkbox ${isSelected ? 'selected' : ''}`}>
                              {isSelected && (
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                  <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </div>
                          )}
                          <span className="customize-option-label">{modifier.name}</span>
                        </div>
                        <span className="customize-option-price">
                          {parseFloat(modifier.price) > 0 ? formatPrice(modifier.price) : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {hasModifierGroups && hasUngroupedExtras && (
            <div className="customize-section">
              <div className="customize-section-header">
                <h3 className="customize-section-title">Other Extras</h3>
              </div>

              <div className="customize-options-list">
                {ungroupedExtras.map((extra) => {
                  const isSelected = selectedModifiers['ungrouped']?.some(e => e.id === extra.id);
                  const extraPrice = parseFloat(extra.price || 0);

                  return (
                    <div
                      key={extra.id}
                      onClick={() => handleModifierToggle(null, extra)}
                      className={`customize-option-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="customize-option-left">
                        <div className={`customize-checkbox ${isSelected ? 'selected' : ''}`}>
                          {isSelected && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <span className="customize-option-label">
                          {extra.name}
                        </span>
                      </div>

                      <span className="customize-option-price">
                        {extraPrice > 0 ? formatPrice(extraPrice) : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasExtrasFallback && (
            <div className="customize-section">
              <div className="customize-section-header">
                <h3 className="customize-section-title">Extras</h3>
              </div>

              <div className="customize-options-list">
                {product.extras.map((extra) => {
                  const isSelected = selectedModifiers['ungrouped']?.some(e => e.id === extra.id);
                  const extraPrice = parseFloat(extra.price || 0);

                  return (
                    <div
                      key={extra.id}
                      onClick={() => handleModifierToggle(null, extra)}
                      className={`customize-option-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="customize-option-left">
                        <div className={`customize-checkbox ${isSelected ? 'selected' : ''}`}>
                          {isSelected && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <span className="customize-option-label">
                          {extra.name}
                        </span>
                      </div>

                      <span className="customize-option-price">
                        {formatPrice(extraPrice)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasAddons && (
            <div className="customize-section">
              <h3 className="customize-section-title">
                Add-ons
              </h3>

              <div className="customize-options-list">
                {product.addons.map((addon) => {
                  // ✅ FIXED: Use addon.id for unique identification
                  const isSelected = selectedAddons.some(a => a.id === addon.id);
                  const addonPrice = parseFloat(addon.addon_item_price || 0);

                  return (
                    <div
                      key={addon.id}
                      onClick={() => handleAddonToggle(addon)}
                      className={`customize-option-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="customize-option-left">
                        <div className={`customize-checkbox ${isSelected ? 'selected' : ''}`}>
                          {isSelected && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <span className="customize-option-label">
                          {addon.addon_item_name}
                          {addon.variation_names && addon.variation_names.length > 0 && (
                            <div className="addon-variations-tag">
                              {addon.variation_names.map(v => v.name).join(', ')}
                            </div>
                          )}
                        </span>
                      </div>

                      <span className="customize-option-price">
                        {parseFloat(addon.total_flat_price || addon.total || addon.addon_item_price || 0) > 0
                          ? formatPrice(parseFloat(addon.total_flat_price || addon.total || addon.addon_item_price || 0))
                          : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="customize-modal-footer">
          {showValidation && !isValid && (
            <div className="customize-validation-message">
              Please complete all required selections
            </div>
          )}
          <button
            className={`customize-add-btn ${!isValid ? 'disabled' : ''}`}
            onClick={() => {
              if (!isValid) {
                setShowValidation(true);
              } else {
                handleAddToCart();
              }
            }}
          >
            {!isValid
              ? 'Complete Your Selection'
              : (isEditing
                ? `Update Item - ${formatPrice(cachedTotal)}`
                : `Add to Cart - ${formatPrice(cachedTotal)}`
              )
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomizationModal;